import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { formatDate } from '../adminFormatters';

// Week-four conversation: Q6 fit (1-5), Q7 outcome, Q8 reason. Same coded answers every time.
const OUTCOMES = [
  ['', 'Not yet'],
  ['joined', 'Joined'],
  ['declined', 'Declined'],
  ['undecided', 'Undecided'],
];
const REASONS = [
  ['', 'No reason (a yes)'],
  ['money', 'Money'],
  ['time', 'Time'],
  ['season_of_life', 'Season of life'],
  ['didnt_connect_with_men', "Didn't connect with the men"],
  ['didnt_connect_with_format', "Didn't connect with the format"],
  ['not_what_i_thought', 'Not what I thought it was'],
  ['other', 'Other'],
];

const selectClass =
  'rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white';

const GuestMonthRow = ({ month, adminFetch, onSaved }) => {
  const [draft, setDraft] = useState({
    call_booked: Boolean(month.call_booked),
    fit_score: month.fit_score ?? '',
    outcome: month.outcome ?? '',
    decline_reason: month.decline_reason ?? '',
    fathom_url: month.fathom_url ?? '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const response = await adminFetch(`/admin/guest-months/${month.guest_month_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_booked: draft.call_booked,
          fit_score: draft.fit_score === '' ? null : Number(draft.fit_score),
          outcome: draft.outcome || null,
          decline_reason: draft.decline_reason || null,
          fathom_url: draft.fathom_url || null,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || 'Could not save');
        return;
      }
      toast.success('Saved');
      onSaved();
    } catch (error) {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const ended = !month.active;
  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-[#2a2a32] dark:bg-[#16161a]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{month.name || month.email}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{month.email}</div>
          <div className="text-xs text-gray-500">
            Invited by {month.invited_by_name || 'Unknown'} · started {formatDate(month.started_at)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={ended ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}>
            {ended ? 'Month ended' : `Week ${month.week} of 4`}
          </Badge>
          <Badge className={draft.call_booked ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
            {draft.call_booked ? 'Call booked' : 'Call not booked'}
          </Badge>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={draft.call_booked}
            onChange={(e) => setDraft({ ...draft, call_booked: e.target.checked })}
          />
          Booked
        </label>
        <label className="text-gray-700 dark:text-gray-300">
          Fit (Q6)
          <select
            className={`ml-2 ${selectClass}`}
            value={draft.fit_score}
            onChange={(e) => setDraft({ ...draft, fit_score: e.target.value })}
          >
            <option value="">–</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <label className="text-gray-700 dark:text-gray-300">
          Outcome (Q7)
          <select
            className={`ml-2 ${selectClass}`}
            value={draft.outcome}
            onChange={(e) => setDraft({ ...draft, outcome: e.target.value })}
          >
            {OUTCOMES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="text-gray-700 dark:text-gray-300">
          Reason (Q8)
          <select
            className={`ml-2 ${selectClass}`}
            value={draft.decline_reason}
            onChange={(e) => setDraft({ ...draft, decline_reason: e.target.value })}
          >
            {REASONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <Input
          className="max-w-xs bg-white text-gray-900 dark:bg-[#16161a] dark:text-white"
          placeholder="Fathom recording link"
          value={draft.fathom_url}
          onChange={(e) => setDraft({ ...draft, fathom_url: e.target.value })}
        />
        <Button type="button" onClick={save} disabled={saving} className="bg-aqua text-charcoal hover:bg-aqua-dark">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </div>
  );
};

const TopUpCard = ({ adminFetch }) => {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState(1);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [grants, setGrants] = useState([]);

  const loadGrants = useCallback(async () => {
    try {
      const response = await adminFetch('/admin/invitation-grants');
      if (response.ok) {
        const data = await response.json();
        setGrants(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load invitation grants:', error);
    }
  }, [adminFetch]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await adminFetch('/admin/invitation-grants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), amount: Number(amount), note: note || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data?.detail || 'Could not add invitations');
        return;
      }
      toast.success(`Gave ${data.user_name || 'member'} ${data.amount} more`);
      setEmail('');
      setNote('');
      setAmount(1);
      loadGrants();
    } catch (error) {
      toast.error('Could not add invitations');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
      <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Give a member more invitations</h3>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Every member has 4 for life. Use this when someone has spent all four and still has good prospects. Only admins can do this, and each grant is logged.
      </p>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <Input
          type="email"
          required
          placeholder="Member's email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="max-w-xs bg-white text-gray-900 dark:bg-[#16161a] dark:text-white"
        />
        <select className={selectClass} value={amount} onChange={(e) => setAmount(e.target.value)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>+{n}</option>
          ))}
        </select>
        <Input
          placeholder="Why (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="max-w-xs bg-white text-gray-900 dark:bg-[#16161a] dark:text-white"
        />
        <Button type="submit" disabled={!email || saving} className="bg-aqua text-charcoal hover:bg-aqua-dark">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Add
        </Button>
      </form>
      {grants.length > 0 ? (
        <ul className="mt-4 space-y-1 text-sm text-gray-600 dark:text-gray-400">
          {grants.slice(0, 10).map((grant) => (
            <li key={grant.grant_id}>
              {formatDate(grant.created_at)} · {grant.granted_by_name || 'Admin'} gave {grant.user_name || grant.user_id} +{grant.amount}
              {grant.note ? ` (${grant.note})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
};

const GuestMonthsPanel = ({ adminFetch }) => {
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch('/admin/guest-months');
      if (!response.ok) throw new Error('Could not load guest months');
      const data = await response.json();
      setMonths(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Could not load guest months');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Guest months</h3>
          <Button type="button" variant="outline" onClick={load}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-aqua" /></div>
        ) : error ? (
          <div className="py-4 text-sm text-red-600">
            {error}{' '}
            <button type="button" className="underline" onClick={load}>Try again</button>
          </div>
        ) : months.length === 0 ? (
          <div className="py-4 text-sm text-gray-600 dark:text-gray-400">No guest months yet.</div>
        ) : (
          <div className="space-y-3">
            {months.map((month) => (
              <GuestMonthRow key={month.guest_month_id} month={month} adminFetch={adminFetch} onSaved={load} />
            ))}
          </div>
        )}
      </Card>
      <TopUpCard adminFetch={adminFetch} />
    </>
  );
};

export default GuestMonthsPanel;
